/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { StatusContext } from './StatusContext';
import { MetadataContext } from './MetadataContext';
import {
    getMachineStatus, runFbotBuildAction, terraformListMachines, terraformListSiteNames,
    terraformSetEnabled,
    MachineStatus, TerraformMachine,
} from '../data';

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const T = {
    bg:          '#111318',
    card:        '#171A21',
    cardEl:      '#1D212B',
    border:      '#2A3140',
    borderFocus: '#3D4F6B',
    textPrimary: '#E6EAF2',
    textMuted:   '#98A2B3',
    textFaint:   '#5A6478',
    success:     '#22C55E',
    warning:     '#F59E0B',
    danger:      '#EF4444',
    action:      '#60A5FA',
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtBytes(bytes: number | undefined, unit: 'MB' | 'GB'): string {
    if (bytes === undefined) return '—';
    const div = unit === 'GB' ? 1_073_741_824 : 1_048_576;
    return `${(bytes / div).toFixed(1)} ${unit}`;
}

function fmtTs(iso: string | undefined): string {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtTime(ts: string | number | undefined): string {
    if (ts === undefined || ts === null) return '—';
    try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return '—'; }
}

function lastBackupDisplay(iso: string | undefined): { value: string; missing: boolean } {
    if (!iso) return { value: 'missing', missing: true };
    try { return { value: new Date(iso).toLocaleString(), missing: false }; } catch { return { value: iso, missing: false }; }
}

function usagePct(free: number | undefined, total: number | undefined): number | null {
    if (free === undefined || total === undefined || total === 0) return null;
    return Math.round(((total - free) / total) * 100);
}

function barColor(pct: number): string {
    if (pct >= 85) return T.danger;
    if (pct >= 70) return T.warning;
    return T.success;
}

function stateColor(state: string): string {
    switch (state.toLowerCase()) {
        case 'running':  return T.success;
        case 'stopped':  return T.warning;
        case 'stopping': return T.warning;
        case 'pending':  return T.action;
        default:         return T.textMuted;
    }
}

function machineKey(m: TerraformMachine): string {
    return m.tfvarsKey || m.name;
}

// ---------------------------------------------------------------------------
// Primitive components
// ---------------------------------------------------------------------------

function Chip({ label, color }: { label: string; color: string }) {
    return (
        <span style={{
            display: 'inline-block',
            fontSize: '0.68rem',
            fontWeight: 600,
            padding: '2px 6px',
            borderRadius: 3,
            letterSpacing: '0.03em',
            color,
            background: `${color}18`,
            border: `1px solid ${color}44`,
            whiteSpace: 'nowrap',
        }}>{label}</span>
    );
}

function MiniBar({ pct }: { pct: number }) {
    return (
        <div style={{ height: 3, background: T.border, borderRadius: 2, marginTop: 3, minWidth: 40 }}>
            <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: barColor(pct), borderRadius: 2 }} />
        </div>
    );
}

function MetricTile({ label, value, pct }: { label: string; value: string; pct?: number | null }) {
    return (
        <div style={{ background: T.cardEl, borderRadius: 5, padding: '8px 10px' }}>
            <div style={{ color: T.textMuted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
            <div style={{ color: T.textPrimary, fontSize: '0.85rem', fontWeight: 500 }}>{value}</div>
            {pct !== null && pct !== undefined && <MiniBar pct={pct} />}
        </div>
    );
}

function LastBackupTile({ iso }: { iso: string | undefined }) {
    const backup = lastBackupDisplay(iso);
    return (
        <div style={{ background: T.cardEl, borderRadius: 5, padding: '8px 10px' }}>
            <div style={{ color: T.textMuted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Last Backup</div>
            <div style={{ color: backup.missing ? T.danger : T.textPrimary, fontSize: '0.85rem', fontWeight: 500 }}>{backup.value}</div>
        </div>
    );
}

function parseLoginHistoryLine(line: string): { at: string; user: string; ip: string; event: string } {
    const [at = '', user = '', ip = '', event = ''] = String(line || '').split('|');
    return {
        at: at || '—',
        user: user || '—',
        ip: ip || '—',
        event: event || '—',
    };
}

function MetaRow({ label, value, mono, link }: { label: string; value?: string; mono?: boolean; link?: boolean }) {
    const valStyle: React.CSSProperties = {
        color: link ? T.action : T.textPrimary,
        fontSize: '0.8rem',
        fontFamily: mono ? 'monospace' : undefined,
        wordBreak: 'break-all',
        textDecoration: link ? 'none' : undefined,
    };
    return (
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '3px 0', borderBottom: `1px solid ${T.border}33` }}>
            <span style={{ color: T.textMuted, fontSize: '0.72rem', minWidth: 90, flexShrink: 0 }}>{label}</span>
            {link && value
                ? <a href={value} target="_blank" rel="noreferrer" style={valStyle}>{value}</a>
                : <span style={valStyle}>{value || '—'}</span>}
        </div>
    );
}

function Btn({
    children, onClick, disabled, variant = 'ghost', color = T.action, title,
}: {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    variant?: 'solid' | 'ghost' | 'danger';
    color?: string;
    title?: string;
}) {
    const base: React.CSSProperties = {
        border: 'none',
        borderRadius: 5,
        fontSize: '0.78rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '5px 12px',
        opacity: disabled ? 0.4 : 1,
        transition: 'opacity 0.15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
    };
    if (variant === 'solid')  return <button title={title} style={{ ...base, background: color, color: '#fff' }} onClick={onClick} disabled={disabled}>{children}</button>;
    if (variant === 'danger') return <button title={title} style={{ ...base, background: 'transparent', color: T.danger, border: `1px solid ${T.danger}44` }} onClick={onClick} disabled={disabled}>{children}</button>;
    return <button title={title} style={{ ...base, background: 'transparent', color, border: `1px solid ${color}44` }} onClick={onClick} disabled={disabled}>{children}</button>;
}

// ---------------------------------------------------------------------------
// FleetSummaryBar
// ---------------------------------------------------------------------------

function FleetSummaryBar({ machines, statuses }: { machines: TerraformMachine[]; statuses: Map<string, MachineStatus> }) {
    const running  = machines.filter(m => m.instanceState?.toLowerCase() === 'running').length;
    const online   = machines.filter(m => m.publicIp && statuses.get(m.publicIp)?.connectivity === 'ok').length;
    const stopped  = machines.filter(m => ['stopped', 'stopping'].includes(m.instanceState?.toLowerCase() ?? '')).length;
    const disabled = machines.filter(m => !m.enabled).length;

    const items = [
        { label: 'Running',  value: running,           color: T.success },
        { label: 'Online',   value: online,            color: T.action },
        { label: 'Stopped',  value: stopped,           color: T.warning },
        { label: 'Disabled', value: disabled,          color: T.textMuted },
        { label: 'Total',    value: machines.length,   color: T.textPrimary },
    ];

    return (
        <div style={{
            display: 'flex', gap: 0,
            background: T.cardEl, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: '10px 16px', marginBottom: 16, flexWrap: 'wrap',
        }}>
            {items.map((item, i) => (
                <div key={item.label} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: '0 20px',
                    borderLeft: i > 0 ? `1px solid ${T.border}` : 'none',
                }}>
                    <span style={{ color: item.color, fontWeight: 700, fontSize: '1.3rem', lineHeight: 1 }}>{item.value}</span>
                    <span style={{ color: T.textMuted, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 3 }}>{item.label}</span>
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// DetailDrawer
// ---------------------------------------------------------------------------

type ActionId = 'first_install' | 'push_release' | 'fetch_local_db' | 'push_local';

interface DetailDrawerProps {
    machine: TerraformMachine;
    status: MachineStatus | null;
    actionRunning: string | null;
    actionError: string | null;
    togglingEnabled: boolean;
    onToggleEnabled: () => void;
    onAction: (action: ActionId, label: string) => void;
    onDismissError: () => void;
    onClose: () => void;
}

function DetailDrawer({
    machine, status, actionRunning, actionError, togglingEnabled,
    onToggleEnabled, onAction, onDismissError, onClose,
}: DetailDrawerProps) {
    const isBusy   = togglingEnabled || actionRunning !== null;
    const noIp     = !machine.publicIp;
    const showPoll = machine.enabled && !!machine.publicIp;

    const memPct  = usagePct(status?.memFree, status?.memTotal);
    const diskPct = usagePct(status?.diskFree, status?.diskTotal);

    const connectivity = status?.connectivity;
    const connLabel = !showPoll ? 'Not Polled'
        : !status             ? '…'
        : connectivity === 'ok' ? 'Online'
        : connectivity === 'unreachable' ? 'Unreachable' : 'Error';
    const connColor = !showPoll || !status ? T.textMuted
        : connectivity === 'ok' ? T.success
        : connectivity === 'unreachable' ? T.danger : T.warning;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 900 }}
            />
            {/* Drawer */}
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
                background: T.card, borderLeft: `1px solid ${T.border}`,
                boxShadow: '-6px 0 32px rgba(0,0,0,0.5)',
                zIndex: 901, display: 'flex', flexDirection: 'column',
                overflowY: 'auto',
            }}>
                {/* Drawer header */}
                <div style={{ padding: '16px 20px 12px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ color: T.textPrimary, fontWeight: 600, fontSize: '1rem' }}>
                                {machine.tfvarsKey || machine.name}
                            </div>
                            {machine.endpoint
                                ? <a href={machine.endpoint} target="_blank" rel="noreferrer" style={{ color: T.action, fontSize: '0.75rem', textDecoration: 'none' }}>{machine.endpoint}</a>
                                : <span style={{ color: T.textMuted, fontSize: '0.75rem' }}>No endpoint</span>}
                        </div>
                        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: '1.1rem', cursor: 'pointer', padding: '0 0 0 12px', lineHeight: 1 }}>✕</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {machine.instanceState && <Chip label={machine.instanceState} color={stateColor(machine.instanceState)} />}
                        <Chip label={connLabel} color={connColor} />
                        <Chip label={machine.enabled ? 'TF Enabled' : 'TF Disabled'} color={machine.enabled ? T.success : T.textMuted} />
                    </div>
                </div>

                <div style={{ padding: '16px 20px', flexGrow: 1 }}>
                    {/* Health metrics */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ color: T.textMuted, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Health</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <MetricTile label="Version" value={status?.version || '—'} />
                            <LastBackupTile iso={status?.lastBackup} />
                            <MetricTile
                                label="Memory Used"
                                value={status?.memTotal ? `${fmtBytes(status.memFree, 'MB')} free / ${fmtBytes(status.memTotal, 'MB')}` : '—'}
                                pct={memPct}
                            />
                            <MetricTile
                                label="Disk Used"
                                value={status?.diskTotal ? `${fmtBytes(status.diskFree, 'GB')} free / ${fmtBytes(status.diskTotal, 'GB')}` : '—'}
                                pct={diskPct}
                            />
                        </div>
                        {status && (
                            <div style={{ color: T.textFaint, fontSize: '0.65rem', marginTop: 6 }}>
                                Polled at {new Date(status.fetchedAt).toLocaleTimeString()}
                            </div>
                        )}
                    </div>

                    {/* Identity */}
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ color: T.textMuted, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Identity</div>
                        <MetaRow label="Region"      value={machine.region} />
                        <MetaRow label="Instance ID" value={machine.instanceId} mono />
                        <MetaRow label="Type"        value={machine.instanceType} mono />
                        <MetaRow label="AMI"         value={machine.ami} mono />
                        <MetaRow label="Public IP"   value={machine.publicIp} mono />
                        <MetaRow label="Private IP"  value={machine.privateIp} mono />
                        <MetaRow label="Endpoint"    value={machine.endpoint} link />
                        <MetaRow label="AWS Tag"     value={machine.tags?.Name} />
                        {machine.resourceType && <MetaRow label="Resource"   value={machine.resourceType} />}
                    </div>

                    {/* Audit */}
                    {status && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ color: T.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
                                Audit ({status.lastLogins?.length ?? 0})
                            </div>
                            {status.lastLogins?.length ? (
                                <div style={{
                                    background: T.cardEl,
                                    borderRadius: 4,
                                    overflowX: 'auto',
                                    overflowY: 'auto',
                                    maxHeight: 172,
                                    border: `1px solid ${T.border}66`,
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.66rem', textAlign: 'left', borderBottom: `1px solid ${T.border}66`, whiteSpace: 'nowrap' }}>At</th>
                                                <th style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.66rem', textAlign: 'left', borderBottom: `1px solid ${T.border}66`, whiteSpace: 'nowrap' }}>User</th>
                                                <th style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.66rem', textAlign: 'left', borderBottom: `1px solid ${T.border}66`, whiteSpace: 'nowrap' }}>IP</th>
                                                <th style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.66rem', textAlign: 'left', borderBottom: `1px solid ${T.border}66`, whiteSpace: 'nowrap' }}>Event</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {status.lastLogins.map((line, idx) => {
                                                const row = parseLoginHistoryLine(line);
                                                return (
                                                    <tr key={`${line}-${idx}`}>
                                                        <td style={{ padding: '6px 8px', color: T.textPrimary, fontSize: '0.7rem', borderBottom: `1px solid ${T.border}44`, whiteSpace: 'nowrap' }}>{row.at}</td>
                                                        <td style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.7rem', borderBottom: `1px solid ${T.border}44`, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{row.user}</td>
                                                        <td style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.7rem', borderBottom: `1px solid ${T.border}44`, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{row.ip}</td>
                                                        <td style={{ padding: '6px 8px', color: T.textMuted, fontSize: '0.7rem', borderBottom: `1px solid ${T.border}44`, whiteSpace: 'nowrap' }}>{row.event}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ marginTop: 6, padding: '8px 10px', background: T.cardEl, borderRadius: 4, fontSize: '0.68rem', color: T.textMuted }}>
                                    None recorded
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Drawer action bar */}
                <div style={{ padding: '12px 20px 20px', borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
                    {actionError && (
                        <div style={{ background: `${T.danger}18`, border: `1px solid ${T.danger}44`, borderRadius: 4, padding: '6px 10px', marginBottom: 10, fontSize: '0.73rem', color: T.danger, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span style={{ wordBreak: 'break-word' }}>{actionError}</span>
                            <button onClick={onDismissError} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 0, flexShrink: 0 }}>✕</button>
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                        <Btn variant="solid" color={T.action} disabled={noIp || isBusy} onClick={() => onAction('push_release', 'Deploy Release')}>
                            {actionRunning === 'push_release' ? <><Spinner size="sm" /> Deploying…</> : 'Deploy Release'}
                        </Btn>
                        <Btn variant="ghost" color={T.action} disabled={noIp || isBusy} onClick={() => onAction('first_install', 'Deploy First')}>
                            {actionRunning === 'first_install' ? <Spinner size="sm" /> : 'Deploy First'}
                        </Btn>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Btn variant="ghost" color={T.textMuted} disabled={noIp || isBusy} onClick={() => onAction('fetch_local_db', 'Backup DB')}>
                            {actionRunning === 'fetch_local_db' ? <Spinner size="sm" /> : 'Backup DB'}
                        </Btn>
                        <Btn variant="ghost" color={T.textMuted} disabled={noIp || isBusy} onClick={() => onAction('push_local', 'Restore DB')}>
                            {actionRunning === 'push_local' ? <Spinner size="sm" /> : 'Restore DB'}
                        </Btn>
                        <div style={{ marginLeft: 'auto' }}>
                            <Btn
                                variant={machine.enabled ? 'danger' : 'ghost'}
                                color={machine.enabled ? T.danger : T.success}
                                disabled={isBusy}
                                onClick={onToggleEnabled}
                            >
                                {togglingEnabled ? <Spinner size="sm" /> : machine.enabled ? 'Disable' : 'Enable'}
                            </Btn>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------
// Machine table
// ---------------------------------------------------------------------------

type SortCol = 'name' | 'state' | 'region' | 'version' | 'mem' | 'disk' | 'backup';

const COL_LABELS: Record<SortCol, string> = {
    name: 'Machine', state: 'State', region: 'Region',
    version: 'Version', mem: 'Memory', disk: 'Disk', backup: 'Last Backup',
};

function thStyle(active: boolean): React.CSSProperties {
    return {
        padding: '7px 12px',
        color: active ? T.textPrimary : T.textMuted,
        fontSize: '0.68rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        cursor: 'pointer',
        background: T.cardEl,
        borderBottom: `1px solid ${T.border}`,
        textAlign: 'left' as const,
    };
}

interface MachineTableProps {
    machines: TerraformMachine[];
    statuses: Map<string, MachineStatus>;
    actionRunningMap: Map<string, string>;
    selectedKey: string | null;
    onSelect: (m: TerraformMachine) => void;
}

function MachineTable({ machines, statuses, actionRunningMap, selectedKey, onSelect }: MachineTableProps) {
    const [sort, setSort] = useState<{ col: SortCol; dir: 1 | -1 }>({ col: 'name', dir: 1 });

    function toggleSort(col: SortCol) {
        setSort(s => s.col === col ? { col, dir: s.dir === 1 ? -1 : 1 } : { col, dir: 1 });
    }

    function sortedMachines() {
        return [...machines].sort((a, b) => {
            const sa = statuses.get(a.publicIp);
            const sb = statuses.get(b.publicIp);
            let va: string | number = '';
            let vb: string | number = '';
            switch (sort.col) {
                case 'name':    va = machineKey(a); vb = machineKey(b); break;
                case 'state':   va = a.instanceState || ''; vb = b.instanceState || ''; break;
                case 'region':  va = a.region || ''; vb = b.region || ''; break;
                case 'version': va = sa?.version || ''; vb = sb?.version || ''; break;
                case 'mem':     va = usagePct(sa?.memFree, sa?.memTotal) ?? -1; vb = usagePct(sb?.memFree, sb?.memTotal) ?? -1; break;
                case 'disk':    va = usagePct(sa?.diskFree, sa?.diskTotal) ?? -1; vb = usagePct(sb?.diskFree, sb?.diskTotal) ?? -1; break;
                case 'backup':  va = sa?.lastBackup || ''; vb = sb?.lastBackup || ''; break;
            }
            if (va < vb) return -sort.dir;
            if (va > vb) return sort.dir;
            return 0;
        });
    }

    function SortTh({ col, children }: { col: SortCol; children?: React.ReactNode }) {
        const active = sort.col === col;
        return (
            <th style={thStyle(active)} onClick={() => toggleSort(col)}>
                {children ?? COL_LABELS[col]}
                {active && <span style={{ marginLeft: 4, opacity: 0.7 }}>{sort.dir === 1 ? '↑' : '↓'}</span>}
            </th>
        );
    }

    return (
        <div style={{ overflowX: 'auto', border: `1px solid ${T.border}`, borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <SortTh col="name" />
                        <SortTh col="state" />
                        <th style={{ ...thStyle(false), cursor: 'default' }}>Connectivity</th>
                        <SortTh col="region" />
                        <SortTh col="version" />
                        <SortTh col="mem">Memory</SortTh>
                        <SortTh col="disk">Disk</SortTh>
                        <SortTh col="backup">Last Backup</SortTh>
                        <th style={{ ...thStyle(false), cursor: 'default' }}>Polled</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedMachines().map(m => {
                        const key    = machineKey(m);
                        const status = m.publicIp ? statuses.get(m.publicIp) ?? null : null;
                        const showPoll = m.enabled && !!m.publicIp;
                        const isSelected = selectedKey === key;
                        const isRunning  = actionRunningMap.has(key);

                        const memPct  = usagePct(status?.memFree, status?.memTotal);
                        const diskPct = usagePct(status?.diskFree, status?.diskTotal);

                        const connectivity = status?.connectivity;
                        const connLabel = !showPoll ? 'Not Polled'
                            : !status ? '…'
                            : connectivity === 'ok' ? 'Online'
                            : connectivity === 'unreachable' ? 'Unreachable' : 'Error';
                        const connColor = !showPoll || !status ? T.textFaint
                            : connectivity === 'ok' ? T.success
                            : connectivity === 'unreachable' ? T.danger : T.warning;

                        return (
                            <tr
                                key={key}
                                onClick={() => onSelect(m)}
                                style={{
                                    background: isSelected ? `${T.action}12` : 'transparent',
                                    borderBottom: `1px solid ${T.border}`,
                                    cursor: 'pointer',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = `${T.cardEl}`; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? `${T.action}12` : 'transparent'; }}
                            >
                                <td style={{ padding: '9px 12px', color: T.textPrimary, fontWeight: 500, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                    {isRunning && <Spinner size="sm" style={{ marginRight: 6, opacity: 0.6 }} />}
                                    {key || <span style={{ color: T.textFaint }}>—</span>}
                                </td>
                                <td style={{ padding: '9px 12px' }}>
                                    {m.instanceState
                                        ? <Chip label={m.instanceState} color={stateColor(m.instanceState)} />
                                        : <span style={{ color: T.textFaint, fontSize: '0.75rem' }}>—</span>}
                                </td>
                                <td style={{ padding: '9px 12px' }}>
                                    <Chip label={connLabel} color={connColor} />
                                </td>
                                <td style={{ padding: '9px 12px', color: T.textMuted, fontSize: '0.8rem' }}>{m.region || '—'}</td>
                                <td style={{ padding: '9px 12px', color: T.textMuted, fontSize: '0.8rem', fontFamily: 'monospace' }}>{status?.version || '—'}</td>
                                <td style={{ padding: '9px 12px', minWidth: 80 }}>
                                    {memPct !== null
                                        ? <><span style={{ color: memPct >= 85 ? T.danger : memPct >= 70 ? T.warning : T.textMuted, fontSize: '0.75rem' }}>{memPct}%</span><MiniBar pct={memPct} /></>
                                        : <span style={{ color: T.textFaint, fontSize: '0.75rem' }}>—</span>}
                                </td>
                                <td style={{ padding: '9px 12px', minWidth: 80 }}>
                                    {diskPct !== null
                                        ? <><span style={{ color: diskPct >= 85 ? T.danger : diskPct >= 70 ? T.warning : T.textMuted, fontSize: '0.75rem' }}>{diskPct}%</span><MiniBar pct={diskPct} /></>
                                        : <span style={{ color: T.textFaint, fontSize: '0.75rem' }}>—</span>}
                                </td>
                                <td style={{
                                    padding: '9px 12px',
                                    color: status?.lastBackup ? T.textMuted : T.danger,
                                    fontSize: '0.75rem',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {status?.lastBackup ? fmtTs(status.lastBackup) : 'missing'}
                                </td>
                                <td style={{ padding: '9px 12px', color: T.textFaint, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{fmtTime(status?.fetchedAt)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// OpsPage
// ---------------------------------------------------------------------------

const POLL_INTERVAL = 60_000;
const DEFAULT_SSH_USER = 'rocky';

function OpsPage() {
    const statusContext = useContext(StatusContext);
    const metadata      = useContext<any>(MetadataContext);

    const [machines,         setMachines]         = useState<TerraformMachine[]>([]);
    const [isLoading,        setIsLoading]         = useState(true);
    const [machinesLoaded,   setMachinesLoaded]    = useState(false);
    const [error,            setError]             = useState<string | null>(null);
    const [statuses,         setStatuses]          = useState<Map<string, MachineStatus>>(new Map());
    const [togglingMap,      setTogglingMap]       = useState<Map<string, boolean>>(new Map());
    const [actionRunningMap, setActionRunningMap]  = useState<Map<string, string>>(new Map());
    const [actionErrorMap,   setActionErrorMap]    = useState<Map<string, string>>(new Map());
    const [refreshSuspended, setRefreshSuspended]  = useState(false);
    const [selectedKey,      setSelectedKey]       = useState<string | null>(null);

    const keyPath = '';
    const sshUser = DEFAULT_SSH_USER;

    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mountedRef   = useRef(true);

    const selectedMachine = selectedKey ? machines.find(m => machineKey(m) === selectedKey) ?? null : null;
    const selectedStatus  = selectedMachine?.publicIp ? statuses.get(selectedMachine.publicIp) ?? null : null;

    function stopPolling() {
        if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    }

    function startPolling() {
        stopPolling();
        pollTimerRef.current = setInterval(() => {
            setMachines(current => { pollAll(current); return current; });
        }, POLL_INTERVAL);
    }

    async function loadMachines(): Promise<TerraformMachine[]> {
        setError(null);
        setIsLoading(true);
        try {
            const list = await terraformListMachines();
            if (mountedRef.current) {
                setMachines(list);
                setMachinesLoaded(true);
                statusContext.postInfo(`Terraform: ${list.length} machine${list.length !== 1 ? 's' : ''} loaded`);
            }
            return list;
        } catch (e: any) {
            const msg = e?.message || String(e);
            if (mountedRef.current) {
                setError(msg);
                setMachinesLoaded(true);
                statusContext.postError(`Terraform list failed: ${msg}`);
            }
            return [];
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }

    function pollMachine(m: TerraformMachine) {
        if (!m.enabled || !m.publicIp) return;
        getMachineStatus(m.publicIp, m.tfvarsKey, keyPath, sshUser)
            .then(status => {
                if (!mountedRef.current) return;
                setStatuses(prev => new Map(prev).set(m.publicIp, status));
            })
            .catch(() => {});
    }

    function pollAll(list: TerraformMachine[]) { list.forEach(pollMachine); }

    async function handleRefresh() {
        const list = await loadMachines();
        pollAll(list);
    }

    function handleToggleRefreshSuspended() {
        setRefreshSuspended(prev => {
            const next = !prev;
            if (next) { stopPolling(); statusContext.postInfo('Ops auto-refresh suspended'); }
            else      { startPolling(); statusContext.postInfo('Ops auto-refresh resumed'); }
            return next;
        });
    }

    useEffect(() => {
        mountedRef.current = true;
        terraformListSiteNames().then(names => {
            if (!mountedRef.current) return;
            setMachines(names.map(name => ({
                name, tfvarsKey: name, instanceId: '', instanceType: '', region: '', ami: '',
                publicIp: '', publicDns: '', privateIp: '', endpoint: '', enabled: false,
                instanceState: '', tags: {}, resourceType: '',
            })));
        }).catch(() => {});
        loadMachines().then(list => {
            if (!mountedRef.current) return;
            pollAll(list);
            startPolling();
        });
        return () => { mountedRef.current = false; stopPolling(); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleToggleEnabled(m: TerraformMachine) {
        const key = machineKey(m);
        setTogglingMap(prev => new Map(prev).set(key, true));
        try {
            await terraformSetEnabled(key, !m.enabled);
            statusContext.postInfo(`${key}: terraform ${m.enabled ? 'disabled' : 'enabled'}`);
            const list = await loadMachines();
            pollAll(list);
        } catch (e: any) {
            const msg = e?.message || String(e);
            statusContext.postError(`Terraform set-enabled failed: ${msg.split('\n')[0]}`);
        } finally {
            setTogglingMap(prev => { const n = new Map(prev); n.delete(key); return n; });
        }
    }

    const handleAction = useCallback(async (m: TerraformMachine, action: ActionId, label: string) => {
        const key    = machineKey(m);
        const target = key;
        setActionErrorMap(prev => { const n = new Map(prev); n.delete(key); return n; });
        setActionRunningMap(prev => new Map(prev).set(key, action));
        try {
            await runFbotBuildAction(action, { target, keyPath, sshUser });
            statusContext.postInfo(`${label}: completed for ${key}`);
        } catch (e: any) {
            const msg = e?.message || String(e);
            setActionErrorMap(prev => new Map(prev).set(key, msg));
            statusContext.postError(`${label} failed (${key}): ${msg.split('\n')[0]}`);
        } finally {
            setActionRunningMap(prev => { const n = new Map(prev); n.delete(key); return n; });
        }
    }, [keyPath, sshUser, statusContext]);

    if (!metadata?.opsMode) {
        return (
            <div style={{ padding: '24px', background: T.bg, minHeight: '100%' }}>
                <Alert variant="warning" className="mb-0">
                    Ops tools are not active. Configure Ops mode in Settings to use this page.
                </Alert>
            </div>
        );
    }

    return (
        <div style={{ background: T.bg, minHeight: '100%', padding: '20px 24px' }}>
            {/* Page header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h4 style={{ margin: 0, color: T.textPrimary, fontWeight: 600, fontSize: '1rem' }}>Cloud Machines</h4>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="ghost" color={refreshSuspended ? T.success : T.warning} onClick={handleToggleRefreshSuspended}>
                        {refreshSuspended ? 'Resume Auto Refresh' : 'Suspend Auto Refresh'}
                    </Btn>
                    <Btn variant="ghost" color={T.textMuted} onClick={handleRefresh} disabled={isLoading}>
                        {isLoading ? <><Spinner size="sm" /> Loading…</> : 'Refresh'}
                    </Btn>
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">{error}</Alert>
            )}

            {/* Fleet summary */}
            {machines.length > 0 && (
                <FleetSummaryBar machines={machines} statuses={statuses} />
            )}

            {machinesLoaded && machines.length === 0 && !error && (
                <div style={{ color: T.textMuted, padding: '20px 0' }}>No terraform-managed machines found.</div>
            )}

            {/* Machine table */}
            {machines.length > 0 && (
                <MachineTable
                    machines={machines}
                    statuses={statuses}
                    actionRunningMap={actionRunningMap}
                    selectedKey={selectedKey}
                    onSelect={m => setSelectedKey(k => k === machineKey(m) ? null : machineKey(m))}
                />
            )}

            {/* Detail drawer */}
            {selectedMachine && (
                <DetailDrawer
                    machine={selectedMachine}
                    status={selectedStatus}
                    actionRunning={actionRunningMap.get(machineKey(selectedMachine)) ?? null}
                    actionError={actionErrorMap.get(machineKey(selectedMachine)) ?? null}
                    togglingEnabled={togglingMap.get(machineKey(selectedMachine)) === true}
                    onToggleEnabled={() => handleToggleEnabled(selectedMachine)}
                    onAction={(action, label) => handleAction(selectedMachine, action, label)}
                    onDismissError={() => setActionErrorMap(prev => { const n = new Map(prev); n.delete(machineKey(selectedMachine)); return n; })}
                    onClose={() => setSelectedKey(null)}
                />
            )}
        </div>
    );
}

export default OpsPage;
